import { useState } from "react";
import api from "../../api/axios";

export interface DiscountState {
  discount: number;
  productsSum: number;
  pending: { id: number; amount: number; reason: string | null; requestedBy: string; requestedById: number } | null;
  lastDecision: { id: number; status: "approved" | "rejected"; amount: number; decisionNote: string | null } | null;
}

interface Props {
  dealId: number;
  state: DiscountState | null;
  isAdmin: boolean;
  meId: number | null;
  /** сделка не завершена и пользователь может её править */
  canEdit: boolean;
  onChanged: () => void;
}

const fmt = (n: number) => new Intl.NumberFormat("ru-RU").format(Math.round(n * 100) / 100) + " ₸";

const box: React.CSSProperties = { borderTop: "1px solid #1f2937", padding: "10px 11px", display: "flex", flexDirection: "column", gap: 8 };
const row: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, fontSize: 13, color: "#9ca3af" };
const inp: React.CSSProperties = { background: "#0b0f19", border: "1px solid #1f2937", borderRadius: 8, color: "#e5e7eb", padding: "8px 10px", fontSize: 14, outline: "none", boxSizing: "border-box", width: "100%" };
const chip: React.CSSProperties = { background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", color: "#a5b4fc", borderRadius: 14, padding: "4px 10px", fontSize: 12, cursor: "pointer" };
const btn: React.CSSProperties = { background: "#6366f1", border: "none", color: "#fff", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnGhost: React.CSSProperties = { background: "transparent", border: "1px solid #374151", color: "#9ca3af", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" };

export default function DiscountBox({ dealId, state, isAdmin, meId, canEdit, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!state) return null;
  const { discount, productsSum, pending, lastDecision } = state;
  if (productsSum <= 0 && discount <= 0) return null;

  const num = Number(amount.replace(/\s/g, "").replace(",", ".")) || 0;

  const run = async (fn: () => Promise<any>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setOpen(false);
      setAmount("");
      setReason("");
      onChanged();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setError(Array.isArray(m) ? m.join(", ") : m || "Не удалось выполнить действие");
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (!(num >= 0)) return setError("Укажите сумму скидки");
    if (!isAdmin && num <= 0) return setError("Укажите сумму скидки больше нуля");
    if (!isAdmin && !reason.trim()) return setError("Напишите причину скидки");
    if (num > productsSum) return setError(`Скидка не может быть больше ${fmt(productsSum)}`);
    run(() => api.post("/approvals/discount", { dealId, amount: num, reason: reason.trim() }));
  };

  return (
    <div style={box}>
      <div style={row}>
        <span>Сумма позиций</span>
        <span>{fmt(productsSum)}</span>
      </div>
      {discount > 0 && (
        <div style={{ ...row, color: "#34d399" }}>
          <span>
            🏷 Скидка на чек
            {isAdmin && canEdit && (
              <button
                type="button"
                disabled={busy}
                onClick={() => window.confirm("Снять скидку?") && run(() => api.post("/approvals/discount", { dealId, amount: 0 }))}
                style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 12, marginLeft: 8 }}
              >
                снять
              </button>
            )}
          </span>
          <span>− {fmt(discount)}</span>
        </div>
      )}

      {pending && (
        <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", color: "#fbbf24", borderRadius: 8, padding: "8px 10px", fontSize: 12.5, lineHeight: 1.5 }}>
          ⏳ Скидка {fmt(pending.amount)} ожидает согласования администратора
          {pending.reason ? <> · «{pending.reason}»</> : null}
          {(isAdmin || pending.requestedById === meId) && (
            <div style={{ marginTop: 6 }}>
              <button type="button" disabled={busy} style={{ ...btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => run(() => api.post(`/approvals/${pending.id}/cancel`))}>
                Отозвать запрос
              </button>
            </div>
          )}
        </div>
      )}
      {!pending && lastDecision?.status === "rejected" && lastDecision.decisionNote !== "Скидку назначил администратор" && (
        <div style={{ color: "#f87171", fontSize: 12 }}>
          ⛔ Последний запрос на {fmt(lastDecision.amount)} отклонён{lastDecision.decisionNote ? `: ${lastDecision.decisionNote}` : ""}
        </div>
      )}

      {canEdit && !open && (!pending || isAdmin) && (
        <div>
          <button
            type="button"
            style={btnGhost}
            onClick={() => {
              setAmount(discount > 0 ? String(discount) : "");
              setError("");
              setOpen(true);
            }}
          >
            {isAdmin ? (discount > 0 ? "🏷 Изменить скидку" : "🏷 Назначить скидку") : "🏷 Запросить скидку у администратора"}
          </button>
        </div>
      )}

      {canEdit && open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {[3, 5, 10, 15].map((p) => (
              <button key={p} type="button" style={chip} onClick={() => setAmount(String(Math.round((productsSum * p) / 100)))}>
                {p}%
              </button>
            ))}
          </div>
          <input
            style={inp}
            inputMode="decimal"
            placeholder="Сумма скидки на весь чек, ₸"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.,\s]/g, ""))}
            autoFocus
          />
          <textarea
            style={{ ...inp, minHeight: 56, resize: "vertical", fontFamily: "inherit" }}
            placeholder={isAdmin ? "Причина (необязательно)" : "Причина скидки (обязательно)"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
          />
          {num > 0 && num <= productsSum && (
            <div style={{ fontSize: 12.5, color: "#9ca3af" }}>
              К оплате будет: <b style={{ color: "#c084fc" }}>{fmt(productsSum - num)}</b>
            </div>
          )}
          {error && <div style={{ color: "#f87171", fontSize: 12.5 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
              {busy ? "Отправляем…" : isAdmin ? "Применить" : "Отправить на согласование"}
            </button>
            <button type="button" style={btnGhost} onClick={() => setOpen(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}
      {!open && error && <div style={{ color: "#f87171", fontSize: 12.5 }}>{error}</div>}
    </div>
  );
}
